import { useCallback, useMemo } from 'react';
import { useNotifications } from '@/notifications';
import { notificationService } from '@/notifications/NotificationService';

export type NotificationSeverity = 'success' | 'info' | 'warning' | 'error';

export interface NotificationMessage {
  id: string;
  title: string;
  description?: string;
  severity: NotificationSeverity;
  createdAt: string;
  read: boolean;
}

export interface NotificationInput {
  title: string;
  description?: string;
  severity?: NotificationSeverity;
}

interface NotificationContextValue {
  notifications: NotificationMessage[];
  unreadCount: number;
  notify: (
    messageOrPayload: string | NotificationInput,
    severity?: NotificationSeverity,
  ) => void;
  dismiss: (id: string) => void;
  dismissAll: () => void;
  markAllRead: () => void;
  markRead: (id: string) => void;
}

function mapLegacySeverity(
  severity: NotificationSeverity,
): 'info' | 'success' | 'warning' | 'critical' {
  if (severity === 'error') {
    return 'critical';
  }

  return severity;
}

function mapSeverityFromRecord(
  severity: 'info' | 'success' | 'warning' | 'critical',
): NotificationSeverity {
  if (severity === 'critical') {
    return 'error';
  }

  return severity;
}

export function notifyGlobal(
  messageOrPayload: string | NotificationInput,
  severity: NotificationSeverity = 'error',
): void {
  const payload =
    typeof messageOrPayload === 'string'
      ? { title: messageOrPayload }
      : messageOrPayload;

  void notificationService.publish({
    title: payload.title,
    message: payload.description ?? payload.title,
    productId: 'entity-portal',
    productName: 'Entity Portal',
    category: 'system',
    type: severity === 'error' ? 'critical' : 'information',
    severity: mapLegacySeverity(payload.severity ?? severity),
  });
}

export function useNotificationContext(): NotificationContextValue {
  const {
    notifications,
    unreadCount,
    publish,
    remove,
    deleteMany,
    markAllRead,
    markRead,
  } = useNotifications();

  const mappedNotifications = useMemo(
    () =>
      notifications.map((item) => ({
        id: item.id,
        title: item.title,
        description: item.message,
        severity: mapSeverityFromRecord(item.severity),
        createdAt: item.createdAt,
        read: item.read,
      })),
    [notifications],
  );

  const notify = useCallback(
    (
      messageOrPayload: string | NotificationInput,
      severity: NotificationSeverity = 'info',
    ) => {
      const payload =
        typeof messageOrPayload === 'string'
          ? { title: messageOrPayload }
          : messageOrPayload;

      void publish({
        title: payload.title,
        message: payload.description ?? payload.title,
        productId: 'entity-portal',
        productName: 'Entity Portal',
        category: 'system',
        type: severity === 'error' ? 'critical' : 'information',
        severity: mapLegacySeverity(payload.severity ?? severity),
      });
    },
    [publish],
  );

  return {
    notifications: mappedNotifications,
    unreadCount,
    notify,
    dismiss: (id: string) => {
      void remove(id);
    },
    dismissAll: () => {
      void deleteMany(mappedNotifications.map((item) => item.id));
    },
    markAllRead: () => {
      void markAllRead();
    },
    markRead: (id: string) => {
      void markRead(id);
    },
  };
}
